// Popover surfacing class-level evidence for a prescribed medicine:
// renal / hepatic / pregnancy / lactation / paediatric / geriatric / monitoring.
// If an eGFR is available the current renal band is highlighted at the top.

import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { BookOpen, Loader2, ShieldCheck, ShieldAlert, Ban, HeartPulse, Baby, User } from "lucide-react";

const LEVEL_STYLE = {
  safe: { text: "text-emerald-800", bg: "bg-emerald-50 border-emerald-200", chip: "bg-emerald-600", Icon: ShieldCheck },
  caution: { text: "text-amber-800", bg: "bg-amber-50 border-amber-200", chip: "bg-amber-500", Icon: ShieldAlert },
  contraindicated: { text: "text-red-800", bg: "bg-red-50 border-red-200", chip: "bg-red-600", Icon: Ban },
};

const Row = ({ label, children }) => (
  <div className="grid grid-cols-[80px_1fr] gap-2 text-[11px]">
    <div className="text-slate-500 uppercase tracking-wider text-[9px] font-semibold pt-0.5">{label}</div>
    <div className="text-slate-800">{children}</div>
  </div>
);

export default function DrugEvidencePopover({ brand, generic, composition, egfr, testid = "drug-evidence" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (brand) params.set("brand", brand);
    if (generic) params.set("generic", generic);
    if (composition) params.set("composition", composition);
    if (egfr !== undefined && egfr !== null && egfr !== "") params.set("egfr", String(egfr));
    api.get(`/drug-evidence?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ drug_class: null, evidence: null, message: "Lookup failed" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ev = data?.evidence;
  const renalNow = data?.renal_now;
  const nowStyle = renalNow ? (LEVEL_STYLE[renalNow.level] || LEVEL_STYLE.caution) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="p-1 rounded hover:bg-pink-50 text-slate-500 hover:text-pink-700"
          title="Show class evidence (renal / hepatic / pregnancy)"
          data-testid={`${testid}-trigger`}
        >
          <BookOpen size={13} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-h-[520px] overflow-y-auto p-0">
        <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 sticky top-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Class evidence</div>
          <div className="text-sm font-semibold text-slate-900">
            {data?.class_label || (loading ? "Looking up…" : "—")}
          </div>
          <div className="text-[11px] text-slate-500">{brand}{generic ? ` · ${generic}` : ""}</div>
        </div>
        <div className="p-3 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-4 text-xs text-slate-500">
              <Loader2 size={14} className="animate-spin mr-1.5" /> Fetching…
            </div>
          )}
          {!loading && !ev && !data?.monograph && (
            <div className="text-xs text-slate-500">
              {data?.message || "No class-level evidence yet for this medicine."}
            </div>
          )}
          {renalNow && nowStyle && (
            <div className={`border rounded-md px-2.5 py-2 text-[11px] ${nowStyle.bg}`} data-testid={`${testid}-now`}>
              <div className="flex items-center gap-1.5">
                <nowStyle.Icon size={13} className={nowStyle.text} />
                <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${nowStyle.chip} text-white`}>{renalNow.level}</span>
                <span className={`font-medium ${nowStyle.text}`}>eGFR {egfr}</span>
              </div>
              <div className={`mt-1 ${nowStyle.text}`}>{renalNow.recommendation}</div>
            </div>
          )}
          {ev?.renal && (
            <Row label="Renal">
              <ul className="space-y-1">
                {ev.renal.map((r, i) => {
                  const st = LEVEL_STYLE[r.level] || LEVEL_STYLE.caution;
                  const range = r.egfr_max ? `${r.egfr_min}–${r.egfr_max}` : `≥ ${r.egfr_min}`;
                  return (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded text-white shrink-0 mt-0.5 ${st.chip}`}>{r.level.slice(0, 3)}</span>
                      <span><strong className="tabular">eGFR {range}:</strong> {r.recommendation}</span>
                    </li>
                  );
                })}
              </ul>
            </Row>
          )}
          {ev?.hepatic && (
            <Row label="Hepatic">
              <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded text-white mr-1 ${(LEVEL_STYLE[ev.hepatic.level] || LEVEL_STYLE.caution).chip}`}>{ev.hepatic.level}</span>
              {ev.hepatic.recommendation}
            </Row>
          )}
          {ev?.pregnancy && (
            <Row label="Pregnancy">
              <div><HeartPulse size={11} className="inline text-pink-600 mr-0.5" /> <strong>Category {ev.pregnancy.category}</strong></div>
              <div className="text-slate-600 mt-0.5">{ev.pregnancy.note}</div>
            </Row>
          )}
          {ev?.lactation && (
            <Row label="Lactation">
              <div><strong className="capitalize">{ev.lactation.compatibility}</strong></div>
              <div className="text-slate-600 mt-0.5">{ev.lactation.note}</div>
            </Row>
          )}
          {ev?.pediatric && (
            <Row label="Paediatric">
              <div><Baby size={11} className="inline text-sky-600 mr-0.5" />
                {ev.pediatric.min_age_years !== undefined && ev.pediatric.min_age_years !== null && (
                  <span>From <strong>≥ {ev.pediatric.min_age_years} yr</strong>. </span>
                )}
                {ev.pediatric.dose && <span>{ev.pediatric.dose}</span>}
              </div>
              {ev.pediatric.note && <div className="text-slate-600 mt-0.5">{ev.pediatric.note}</div>}
            </Row>
          )}
          {ev?.geriatric && (
            <Row label="Geriatric">
              <div><User size={11} className="inline text-slate-600 mr-0.5" /> {ev.geriatric.note}</div>
            </Row>
          )}
          {ev?.monitoring && ev.monitoring.length > 0 && (
            <Row label="Monitor">
              <ul className="list-disc pl-4 space-y-0.5">
                {ev.monitoring.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </Row>
          )}
          {data?.monograph && (
            <div className="border-t border-slate-200 pt-2 mt-1 space-y-2" data-testid={`${testid}-monograph`}>
              <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Monograph · reference only</div>
              <Row label="Drug">
                <strong>{data.monograph.generic}</strong>
                {data.monograph.drug_class ? <span className="text-slate-500"> — {data.monograph.drug_class}</span> : null}
              </Row>
              {data.monograph.interactions_text && (
                <Row label="Interactions">{data.monograph.interactions_text}</Row>
              )}
              {data.monograph.pregnancy_category && (
                <Row label="Pregnancy">{data.monograph.pregnancy_category}</Row>
              )}
              {data.monograph.reference && (
                <Row label="Source">{data.monograph.reference}</Row>
              )}
              <div className="text-[9px] text-slate-400 italic">Reference information — not a substitute for clinical judgement.</div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
